import React, { useEffect, useState } from 'react';
import PageWrapper from '../components/PageWrapper';
import { getDocs, collection } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { normalizeType, normalizeGenre, normalizeKey } from '../utils/normalize';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const StatsPage = () => {
  const [stats, setStats] = useState(null);
  const [scatterData, setScatterData] = useState([]);

  useEffect(() => {
    const loadStats = async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      const [tunesSnap, setsSnap, sessionsSnap] = await Promise.all([
        getDocs(collection(db, 'users', uid, 'tunes')),
        getDocs(collection(db, 'users', uid, 'sets')),
        getDocs(collection(db, 'users', uid, 'sessions'))
      ]);

      const tunes = tunesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const sets = setsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const sessions = sessionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const typeCounts = {};
      const genreCounts = {};
      const keyCounts = {};

      const grouped = {};
      for (const t of tunes) {
        const genre = normalizeGenre(t.genre || 'Other');
        const key = normalizeKey(t.key || 'Other');
        const id = `${key}|${genre}`; // ensure each key+genre pair is unique

        if (!grouped[id]) grouped[id] = { x: key, y: genre, z: 0 };
        grouped[id].z += 1;

        const type = normalizeType(t.type);
        if (type) typeCounts[type] = (typeCounts[type] || 0) + 1;
        if (genre) genreCounts[genre] = (genreCounts[genre] || 0) + 1;
        if (key) keyCounts[key] = (keyCounts[key] || 0) + 1;
      }

      const bubbleData = Object.values(grouped);

      const mostCommon = (obj) =>
        Object.entries(obj).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';

      setStats({
        tuneCount: tunes.length,
        setCount: sets.length,
        sessionCount: sessions.length,
        mostCommonType: mostCommon(typeCounts),
        mostCommonGenre: mostCommon(genreCounts),
        mostCommonKey: mostCommon(keyCounts),
      });

      setScatterData(bubbleData);
    };

    loadStats();
  }, []);

  if (!stats) return <PageWrapper><p>Loading stats…</p></PageWrapper>;

  return (
    <PageWrapper>
      <h2 style={{ textAlign: 'center', marginBottom: '1rem' }}>📊 Stats</h2>
      <div style={{ lineHeight: '1.6', fontSize: '1.1rem', maxWidth: '400px', margin: 'auto' }}>
        <div><strong>Total tunes:</strong> {stats.tuneCount}</div>
        <div><strong>Total sets:</strong> {stats.setCount}</div>
        <div><strong>Total sessions:</strong> {stats.sessionCount}</div>
        <div><strong>Most common type:</strong> {stats.mostCommonType}</div>
        <div><strong>Most common genre:</strong> {stats.mostCommonGenre}</div>
        <div><strong>Most common key:</strong> {stats.mostCommonKey}</div>
      </div>

      <div style={{ height: '420px', marginTop: '2rem' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 20, bottom: 10, left: 10 }}>
            <CartesianGrid />
            <XAxis type="category" dataKey="x" name="Key" />
            <YAxis type="category" dataKey="y" name="Genre" />
            <ZAxis type="number" dataKey="z" name="Count" range={[60, 400]} />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(value, name, props) => [`Count: ${props.payload.z}`, `Genre: ${props.payload.y} | Key: ${props.payload.x}`]} />
            <Scatter name="Tunes" data={scatterData} fill="#8884d8" shape="circle" />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </PageWrapper>
  );
};

export default StatsPage;
